import { useSdk } from '~/sdk';
import type {
  ProductAvailabilityNotificationRequest,
  ProductAvailabilityNotificationResponse,
  ProductAvailabilitySubscriptionStatusResponse,
  ProductAvailabilityNotificationError
} from './types';

export const useProductAvailabilityNotification = () => {
  const sdk = useSdk();
  const { t } = useI18n();

  /**
   * Subscribe to product availability notifications
   */
  const subscribe = async (
    productId: number,
    email: string,
    productAttributeId?: number
  ): Promise<ProductAvailabilityNotificationResponse> => {
    try {
      const response = await sdk.commerce.subscribeToProductAvailability({
        id_product: productId,
        customer_email: email,
        id_product_attribute: productAttributeId,
      });

      if (response.success && response.psdata?.success) {
        return {
          success: true,
          message: response.psdata.message || t('product_availability.success_subscribed'),
          data: response.psdata.data
        };
      } else {
        throw new Error(response.psdata?.message || t('product_availability.error_subscription_failed'));
      }
    } catch (error: any) {
      console.error('Product availability subscription error:', error);
      throw {
        message: error.message || t('product_availability.error_subscription_failed'),
        code: error.response?.status
      } as ProductAvailabilityNotificationError;
    }
  };

  /**
   * Check product availability subscription status
   */
  const checkStatus = async (
    productId: number,
    email: string,
    productAttributeId?: number
  ): Promise<ProductAvailabilitySubscriptionStatusResponse> => {
    try {
      const response = await sdk.commerce.checkProductAvailabilitySubscription({
        id_product: productId,
        customer_email: email,
        id_product_attribute: productAttributeId,
      });

      if (response.success && response.psdata) {
        return {
          success: true,
          subscribed: response.psdata.subscribed || false,
          data: response.psdata.data || null
        };
      } else {
        throw new Error(t('product_availability.error_status_check_failed'));
      }
    } catch (error: any) {
      console.error('Product availability status check error:', error);
      throw {
        message: error.message || t('product_availability.error_status_check_failed'),
        code: error.response?.status
      } as ProductAvailabilityNotificationError;
    }
  };

  /**
   * Unsubscribe from product availability notifications
   */
  const unsubscribe = async (
    productId: number,
    email: string,
    productAttributeId?: number
  ): Promise<ProductAvailabilityNotificationResponse> => {
    try {
      const response = await sdk.commerce.unsubscribeFromProductAvailability({
        id_product: productId,
        customer_email: email,
        id_product_attribute: productAttributeId,
      });

      if (response.success && response.psdata?.success) {
        return {
          success: true,
          message: response.psdata.message || t('product_availability.success_unsubscribed')
        };
      } else {
        throw new Error(response.psdata?.message || t('product_availability.error_unsubscribe_failed'));
      }
    } catch (error: any) {
      console.error('Product availability unsubscription error:', error);
      throw {
        message: error.message || t('product_availability.error_unsubscribe_failed'),
        code: error.response?.status
      } as ProductAvailabilityNotificationError;
    }
  };

  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return {
    subscribe,
    checkStatus,
    unsubscribe,
    validateEmail,
  };
};
